import React, { useState } from "react";
import useTranslate from "../../hooks/useTranslate";
import useFeedback from "../../hooks/useFeedback";
import Model from "@tripian/model";
import { PreLoading, Button } from "@tripian/react";
import AppNav from "../../App/AppNav/AppNav";
import { FEEDBACK_PAGE } from "../../constants/ROUTER_PATH_TITLE";
import { useNavigate } from "react-router-dom";

const FeedbackPage = () => {
  const { loadingFeedback, feedbacks, feedbackSend } = useFeedback();
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedSubject, setSelectedSubject] = useState<number>(0);
  const [feedbackMessage, setFeedbackMessage] = useState<string>("");
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const sendFeedback = async () => {
    if (!feedbacks?.mainSubjects || !feedbacks.mainSubjects.length) return;

    setLoading(true);
    try {
      const feedbackData: Model.FeedbackRequest = {
        subjectType: feedbacks.mainSubjects[selectedSubject].subjectType,
        subjectId: feedbacks.mainSubjects[selectedSubject].id,
        desc: feedbackMessage.trim(),
      };

      await feedbackSend(feedbackData);
      setFeedbackMessage("");
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error) {
      console.error("Error sending feedback:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSubject(Number(e.target.value));
  };

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={
        window.tconfig.IMAGE_PATHS.APP_BACKGROUND_IMG_URL
          ? {
              backgroundImage: `url(${window.tconfig.IMAGE_PATHS.APP_BACKGROUND_IMG_URL})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }
          : {}
      }
    >
      {/* Header */}
      <header className="bg-white shadow-sm">
        <AppNav header={FEEDBACK_PAGE.HEADER?.(t("support.feedback.title"))} />
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex flex-col items-center md:items-start space-y-2 mb-4 md:mb-0">
              <h1 className="text-2xl font-bold text-gray-800 text-center md:text-left">
                {t("support.feedback.title")}
              </h1>
              <p className="text-sm text-gray-500">
                {t("support.feedback.subtitle")}
              </p>
            </div>
            <Button
              onClick={() => navigate("/my-feedback")}
              color="primary"
              text={t("support.feedback.viewMyFeedbacks")}
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-sm overflow-hidden">
          {loadingFeedback ? (
            <div className="flex justify-center items-center p-12">
              <PreLoading />
            </div>
          ) : (
            <div className="p-8">
              {showSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-green-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-medium text-green-800 mb-2">
                    {t("support.feedback.success.title")}
                  </h3>
                  <p className="text-green-700">
                    {t("support.feedback.success.message")}
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                      {t("support.feedback.form.title")}
                    </h2>
                    <p className="text-gray-600">
                      {t("support.feedback.form.subtitle")}
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Subject Selection */}
                    <div>
                      <label
                        htmlFor="feedback-subject"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        {t("support.feedback.modal.title")}
                      </label>
                      <div className="relative">
                        <select
                          id="feedback-subject"
                          className="block w-full pl-4 pr-10 py-3 text-base border border-gray-300 focus:outline-none focus:ring-primary-color focus:border-primary-color rounded-lg shadow-sm appearance-none"
                          value={selectedSubject}
                          onChange={handleSubjectChange}
                          disabled={loading}
                        >
                          {feedbacks?.mainSubjects &&
                            feedbacks.mainSubjects.map((subject, index) => (
                              <option key={subject.id} value={index}>
                                {subject.title}
                              </option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Feedback Message */}
                    <div>
                      <label
                        htmlFor="feedback-message"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        {t("support.feedback.modal.messageTitle")}
                      </label>
                      <textarea
                        id="feedback-message"
                        rows={6}
                        className="block w-full px-4 py-3 text-gray-700 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-color focus:border-primary-color"
                        placeholder={t(
                          "support.feedback.modal.messagePlaceholder"
                        )}
                        value={feedbackMessage}
                        onChange={e => setFeedbackMessage(e.target.value)}
                        disabled={loading}
                      ></textarea>
                      <p className="mt-2 text-sm text-gray-500">{`${feedbackMessage.length} character${feedbackMessage.length !== 1 ? "s" : ""}`}</p>
                    </div>

                    {/* Form Actions */}
                    <div className="flex justify-end space-x-4 pt-4">
                      <Button
                        color="secondary"
                        text={t("support.feedback.modal.close")}
                        onClick={() => setFeedbackMessage("")}
                        disabled={loading}
                        className="disabled:opacity-70 disabled:!bg-secondary-color"
                      />
                      <Button
                        color="primary"
                        text={
                          loading
                            ? t("support.feedback.modal.sending")
                            : t("support.feedback.modal.send")
                        }
                        onClick={sendFeedback}
                        disabled={loading || feedbackMessage.trim() === ""}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FeedbackPage;
